import { Overlay } from '@angular/cdk/overlay';
import { Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { IUserInfo } from '../interface/user-info.interface';
import { nanoid } from 'nanoid';
import { filter, first, map, retry } from 'rxjs/operators';

import { CharonRelation } from './core/helpers/charon/charon.enum';
import { SharedDataTags } from './core/helpers/shared-data/shared-data.enum';
import { AnalyticsService } from './core/services/analytics/analytics.service';
import { CaronteService } from './core/services/caronte/caronte.service';
import { StoreService } from './core/services/store/store.service';
import { SessionExpiredModalErrorComponent } from './error-treatment/components/session-expired-modal-error/session-expired-modal-error.component';
import { FullStory, init } from '@fullstory/browser';
import { environment } from '../environments/environment';
import { NavigationEnd, Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

@UntilDestroy({ checkProperties: true })
@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit {
  title = 'plataforma-atacado';
  userInfo: IUserInfo;

  constructor(
    public overlay: Overlay,
    private dialog: MatDialog,
    private storeService: StoreService,
    private caronteService: CaronteService,
    private analyticsService: AnalyticsService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.listenSessionStatus();
    this.getAvailableContexts();
    this.listenUpdateContext();
    this.setInitialDismissedAlerts();

    this.analyticsService.initializeGA4();
    this.initFullStory();
    this.setUserIdentityFullStory();
  }

  /**
   *  Initializes FullStory and tracks page views
   */

  initFullStory() {
    init({ orgId: environment.fullstoryOrgId });

    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        untilDestroyed(this)
      )
      .subscribe((event) => {
        const viewName = event.urlAfterRedirects.split('?')[0];

        FullStory('setProperties', {
          type: 'page',
          properties: {
            pageName: viewName
          }
        });
      });
  }

  /**
   * Sets user identity in full story (operator id)
   */

  setUserIdentityFullStory() {
    const operator = sessionStorage.getItem('operador')?.toLowerCase().substring(3);

    FullStory('setProperties', {
      type: 'user',
      properties: {
        id: operator,
        displayName: operator
      }
    });
  }

  /**
   * Show modal when user session expires without token update
   */
  listenSessionStatus() {
    this.storeService.validSession.subscribe((status) => {
      if (status) {
        return;
      }

      this.dialog.open(SessionExpiredModalErrorComponent, {
        width: '670px',
        height: '315px',
        panelClass: './../../../error-treatment/components/session-expired-modal-error/session-expired-modal-error.component.scss',
        disableClose: true,
        hasBackdrop: true,
        ariaLabel: 'Sessão expirada',
        scrollStrategy: this.overlay.scrollStrategies.noop()
      });
    });
  }

  /**
   * Waits for mfe to confirm valuation, then request to update available contexts
   */
  listenUpdateContext() {
    this.storeService.getSharedData('update-likert').subscribe(() => this.getAvailableContexts());
  }

  /**
   * Gets and exposes through SharedData info about wich microfrontend should display likert
   * inside their flow
   */
  getAvailableContexts() {
    this.storeService.userToken.pipe(first()).subscribe((token) => {
      this.caronteService
        .get(token, CharonRelation.LIKERT_LIST, nanoid(16))
        .pipe(
          map((availableContextList: { contexto: string; deve_exibir: boolean }[]) => {
            return availableContextList
              .map((context) => (context.deve_exibir ? context.contexto : ''))
              .filter((validContext) => validContext);
          }),
          retry(3)
        )
        .subscribe({
          next: (contextList: string[]) => {
            this.storeService.setSharedData(SharedDataTags.LIKERT_CONTEXTS, contextList);
          }
        });
    });
  }

  setInitialDismissedAlerts() {
    this.storeService.setSharedData('dismissed-alerts', ['']);
  }
}
